import { useEffect, useState } from 'react';

import { Box, Image } from '@zillow/constellation-10';

import { useGoogleMapsWebApiHandler_addSignatureAndKeyToUrl } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import ImpreciseLocationAlert from '../ImpreciseLocationAlert';

import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';

const MAP_MARKER = 'https://nodes3cdn.hotpads.com/google-maps-icons/MapMarker_home.png';

export type Props = {
  address: string;
  marginTop?: string | Record<string, string>;
  additionalMarkers?: string;
  size?: string;
  scale?: string | number;
  showImpreciseLocationAlert?: boolean;
  isMultiplexType?: boolean;
  user: Partial<RentalManagerAuthCheckUserInfoWebApiDto> | null;
  isScreenLgGTE: boolean;
};

const DEFAULT_MAP_SIZE = '388x291';

const StaticMap = ({
  address,
  marginTop,
  additionalMarkers,
  size = DEFAULT_MAP_SIZE,
  scale,
  showImpreciseLocationAlert = false,
  isMultiplexType = false,
  user,
  isScreenLgGTE,
}: Props) => {
  const [mapUrl, setMapUrl] = useState('');

  const { mutate: signUrl } = useGoogleMapsWebApiHandler_addSignatureAndKeyToUrl();

  const confirmAddressButtonTitle = isMultiplexType ? 'Yes, continue' : 'Confirm';

  useEffect(() => {
    const encodedAddress = encodeURIComponent(address);
    const mapSize = showImpreciseLocationAlert ? (isScreenLgGTE ? '388x388' : '388x554') : size;
    const gmapsAPIParams = new URLSearchParams({
      center: encodedAddress,
      size: mapSize,
      style: 'feature:poi|visibility:off',
      markers: `${additionalMarkers ? `${additionalMarkers}` : ''}&markers=icon:${MAP_MARKER}|${encodedAddress}`,
      ...(scale && { scale: String(scale) }),
    });

    const url = `https://maps.googleapis.com/maps/api/staticmap?${gmapsAPIParams.toString()}`;

    signUrl(
      { postData: { url } },
      {
        onSuccess: (response: { signedUrl: string }) => {
          setMapUrl(response.signedUrl);
        },
      },
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isScreenLgGTE, showImpreciseLocationAlert, signUrl]);

  return (
    Boolean(mapUrl) && (
      <>
        {showImpreciseLocationAlert ? (
          <Box
            css={{
              position: 'relative',
              display: 'inline-block',
              flex: size !== DEFAULT_MAP_SIZE ? 'none' : { lg: '1 1 0' },
              width: { base: 'fit-content', lg: 'auto' },
            }}
          >
            {user && <ImpreciseLocationAlert confirmAddressButtonTitle={confirmAddressButtonTitle} user={user} />}
            <Image src={mapUrl} alt={address} css={{ marginTop, width: { lg: '100%' } }} />
          </Box>
        ) : (
          <Image src={mapUrl} alt={address} css={{ marginTop }} />
        )}
      </>
    )
  );
};

export default StaticMap;
